/**
 * CDB-TQS API — Agent Toolbox Router (cdb_tqs#306):
 * sources, endpoints, property catalog, invocations and agent tools
 */

import { CDB_TQS_API_BASE_URL, fetchWithLogging, type RequestOptions } from "./http";

/**
 * cdb_tqs#1578 — a client's router sources live in that client's own database,
 * the shared ones in control. The admin views work on the shared sources plus
 * one client's: `null` is the admin's own client (the backend default), any
 * other value the client picked in the Router tab. Every router admin call
 * carries it as `client_id`; without it, a source of another client is a 404.
 *
 * cdb_tqs#1626 — the audit of tool calls lives with the caller's client too,
 * so the invocation history is one client's at a time, picked the same way.
 */
let routerClientScope: string | null = null

export function setRouterClientScope(clientId: string | null): void {
  routerClientScope = clientId
}

export function getRouterClientScope(): string | null {
  return routerClientScope
}

/** `fetchWithLogging` for the router admin API, scoped to the picked client. */
export function routerFetch(url: string, options: RequestOptions = {}) {
  if (!routerClientScope) return fetchWithLogging(url, options)
  if (url.includes('?')) {
    return fetchWithLogging(
      `${url}&client_id=${encodeURIComponent(routerClientScope)}`,
      options,
    )
  }
  return fetchWithLogging(url, {
    ...options,
    params: { ...(options.params ?? {}), client_id: routerClientScope },
  })
}

/** #1255 — template families of auto-generate-templates for Postgres
 *  sources: `structured` = query / find_rows / describe_tables (server-
 *  rendered), `legacy` = the per-table template set. */
export type TemplateFamily = 'structured' | 'legacy'

export const routerApi = {
  async listRouterConnectorTypes(
    enabledOnly: boolean = false,
  ): Promise<import('@/types/toolbox').RouterConnectorTypeMeta[]> {
    const params: Record<string, string | boolean> = {}
    if (enabledOnly) params.enabled_only = enabledOnly
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/router/connector-types`,
      { method: 'GET', params },
    )
    return response.data
  },

  async listRouterSources(opts?: {
    activeOnly?: boolean
    type?: string
  }): Promise<import('@/types/toolbox').RouterSource[]> {
    const params: Record<string, string | boolean> = {}
    if (opts?.activeOnly !== undefined) params.active_only = opts.activeOnly
    if (opts?.type) params.type = opts.type
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources`,
      { method: 'GET', params },
    )
    return response.data
  },

  async getRouterSource(
    id: string,
  ): Promise<import('@/types/toolbox').RouterSource> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${id}`,
      { method: 'GET' },
    )
    return response.data
  },

  async createRouterSource(
    data: import('@/types/toolbox').RouterSourceCreate,
  ): Promise<import('@/types/toolbox').RouterSource> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources`,
      { method: 'POST', body: JSON.stringify(data) },
    )
    return response.data
  },

  async updateRouterSource(
    id: string,
    patch: import('@/types/toolbox').RouterSourceUpdate,
  ): Promise<import('@/types/toolbox').RouterSource> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${id}`,
      { method: 'PUT', body: JSON.stringify(patch) },
    )
    return response.data
  },

  async deleteRouterSource(id: string): Promise<void> {
    await routerFetch(`${CDB_TQS_API_BASE_URL}/router/sources/${id}`, {
      method: 'DELETE',
    })
  },

  /**
   * Read the source's live schema. With `apply=false` (default) the call
   * is a pure read — nothing is persisted. With `apply=true` the result
   * is upserted into `router_source_entities` / `router_source_attributes`
   * (full-replace for the source) and the response carries diff metadata
   * (entities_added/_removed, endpoints_now_broken/_healthy).
   *
   * Doubles as a connection-test in either mode.
   *
   * REST sources currently return 422 (no introspect-equivalent).
   */
  /**
   * Pull instance and property counts from an MCP source's analytics tool
   * onto this source's schema (cdb_tqs#1275). Separate from introspect: it
   * needs a second source to ask, takes ~30 s on a large graphmart, and
   * writes a snapshot rather than schema. Reads only — no profiling.
   */
  async enrichSchemaCounts(
    id: string,
    mcpSourceId: string,
  ): Promise<import('@/types/toolbox').SchemaCountsResult> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${id}/enrich-counts?mcp_source_id=${encodeURIComponent(mcpSourceId)}`,
      { method: 'POST' },
    )
    return response.data
  },

  async introspectRouterSource(
    id: string,
    opts?: { apply?: boolean },
  ): Promise<import('@/types/toolbox').IntrospectionResult> {
    const query = opts?.apply ? '?apply=true' : ''
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${id}/introspect${query}`,
      { method: 'POST' },
    )
    return response.data
  },

  /**
   * Generate the standard auto-template set (list/get_by_id/count/aggregate/
   * search) for every entity of a Postgres source. Idempotent — operation
   * IDs that already exist on the source are skipped, never overwritten.
   * Returns counts + the new endpoint IDs for the bulk-activate UI.
   *
   * With `regenerate=true` (cdb_tqs#377), existing endpoints that are
   * `auto_generated=true` AND `hand_modified=false` are re-rolled to the
   * current template default in-place — endpoint IDs, activation status
   * and admin-set permissions all survive. Hand-edited and admin-authored
   * endpoints are still skipped.
   */
  async autoGenerateRouterTemplates(
    id: string,
    opts?: { regenerate?: boolean; family?: TemplateFamily },
  ): Promise<import('@/types/toolbox').AutoGenerateTemplatesResult> {
    const params: Record<string, boolean | string> = {}
    if (opts?.regenerate) params.regenerate = true
    if (opts?.family) params.family = opts.family
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${id}/auto-generate-templates`,
      { method: 'POST', params },
    )
    return response.data
  },

  async setRouterSourceHealth(
    id: string,
    health: 'healthy' | 'unhealthy' | 'unknown',
  ): Promise<void> {
    // `health` is a bare enum parameter on the route, which FastAPI reads as
    // a QUERY parameter — a JSON body is answered with 422. Nothing called
    // this until #1496, so the mismatch had never been exercised.
    await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${id}/health`,
      { method: 'POST', params: { health } },
    )
  },

  async listSourceEntities(
    sourceId: string,
  ): Promise<import('@/types/toolbox').RouterSourceEntity[]> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${sourceId}/entities`,
      { method: 'GET' },
    )
    return response.data
  },

  /** #1255 — edit the curated entity fields (description, display column). */
  async updateSourceEntity(
    sourceId: string,
    entityId: string,
    patch: import('@/types/toolbox').RouterSourceEntityUpdate,
  ): Promise<import('@/types/toolbox').RouterSourceEntity> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${sourceId}/entities/${entityId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    )
    return response.data
  },

  /** #1294 — edit the curated attribute field (description). */
  async updateSourceAttribute(
    sourceId: string,
    attrId: string,
    patch: import('@/types/toolbox').RouterSourceAttributeUpdate,
  ): Promise<import('@/types/toolbox').RouterSourceAttribute> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${sourceId}/attributes/${attrId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    )
    return response.data
  },

  /**
   * cdb_tqs#2306 — a draft `POST /<EntitySet>` endpoint for one introspected
   * OData table: the picked fields as body params, the table's key fields
   * as response projection. For workflow write steps; activated by an admin.
   */
  async createWriteEndpoint(
    sourceId: string,
    entityId: string,
    fields: string[],
  ): Promise<import('@/types/toolbox').RouterEndpoint> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${sourceId}/entities/${entityId}/write-endpoint`,
      { method: 'POST', body: JSON.stringify({ fields }) },
    )
    return response.data
  },

  async listSourceAttributes(
    sourceId: string,
    entityId: string,
  ): Promise<import('@/types/toolbox').RouterSourceAttribute[]> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${sourceId}/entities/${entityId}/attributes`,
      { method: 'GET' },
    )
    return response.data
  },

  /**
   * Flat list of every attribute across all entities of the source.
   * Used by the Excel-enrichment-profile property-picker (#448) to feed
   * the property-IRI autocomplete in one round-trip.
   */
  async listSourceAttributesAll(
    sourceId: string,
  ): Promise<import('@/types/toolbox').RouterSourceAttribute[]> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${sourceId}/attributes`,
      { method: 'GET' },
    )
    return response.data
  },

  /**
   * Object-property edges between entities (#366 Phase B2). Used by the
   * PredicateSlot Auto-Suggest in the EndpointSlotsEditor — predicate
   * IRIs live in `description` (B2 convention until a dedicated `iri`
   * column lands).
   */
  async listSourceRelations(
    sourceId: string,
  ): Promise<import('@/types/toolbox').RouterSourceRelation[]> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/sources/${sourceId}/relations`,
      { method: 'GET' },
    )
    return response.data
  },

  async listRouterEndpointsBySource(
    sourceId: string,
  ): Promise<import('@/types/toolbox').RouterEndpoint[]> {
    // Backend mountet `/router/endpoints` als top-level prefix mit
    // `source_id` als Query-Param. Sub-Pfad `/sources/{id}/endpoints`
    // existiert nicht.
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints`,
      { method: 'GET', params: { source_id: sourceId } },
    )
    return response.data
  },

  /**
   * Flat list of router endpoints across all sources — used by the
   * central permissions tab. ``status_filter='active'`` mirrors the
   * Tool-Manifest semantics (only active endpoints ship as tools).
   */
  async listAllRouterEndpoints(opts?: {
    status?: 'draft' | 'testing' | 'active'
  }): Promise<import('@/types/toolbox').RouterEndpoint[]> {
    const params: Record<string, string> = {}
    if (opts?.status) params.status_filter = opts.status
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints`,
      { method: 'GET', params },
    )
    return response.data
  },

  async getRouterEndpoint(
    id: string,
  ): Promise<import('@/types/toolbox').RouterEndpoint> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${id}`,
      { method: 'GET' },
    )
    return response.data
  },

  async createRouterEndpoint(
    data: import('@/types/toolbox').RouterEndpointCreate,
  ): Promise<import('@/types/toolbox').RouterEndpoint> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints`,
      { method: 'POST', body: JSON.stringify(data) },
    )
    return response.data
  },

  async updateRouterEndpoint(
    id: string,
    patch: import('@/types/toolbox').RouterEndpointUpdate,
  ): Promise<import('@/types/toolbox').RouterEndpoint> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${id}`,
      { method: 'PUT', body: JSON.stringify(patch) },
    )
    return response.data
  },

  async deleteRouterEndpoint(id: string): Promise<void> {
    await routerFetch(`${CDB_TQS_API_BASE_URL}/router/endpoints/${id}`, {
      method: 'DELETE',
    })
  },

  // -------------------------------------------------------------------------
  // Router Endpoint Permissions
  // -------------------------------------------------------------------------

  async listEndpointPermissions(
    endpointId: string,
  ): Promise<import('@/types/toolbox').RouterEndpointPermission[]> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${endpointId}/permissions`,
      { method: 'GET' },
    )
    return response.data
  },

  async createEndpointPermission(
    endpointId: string,
    data: import('@/types/toolbox').RouterEndpointPermissionCreate,
  ): Promise<import('@/types/toolbox').RouterEndpointPermission> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${endpointId}/permissions`,
      { method: 'POST', body: JSON.stringify(data) },
    )
    return response.data
  },

  async deleteEndpointPermission(
    endpointId: string,
    permissionId: string,
  ): Promise<void> {
    await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${endpointId}/permissions/${permissionId}`,
      { method: 'DELETE' },
    )
  },

  // -------------------------------------------------------------------------
  // Source Property Catalog (#473) — per-source registry that the
  // enrich-skill resolves user-spoken column terms against without an
  // LLM schema-walk on every miss. Admin can manage manual entries +
  // review the auto_derived queue.
  // -------------------------------------------------------------------------

  async listSourcePropertyCatalogEntries(
    sourceId: string,
    opts?: {
      provenance?: import('@/types/toolbox').CatalogProvenance
      /** Multi-provenance filter (#482 Item 2). Combine e.g. `auto_derived`
       *  + `auto_validated` for the review queue. Mutually exclusive with
       *  `provenance` — backend returns 400 if both are set. */
      provenances?: import('@/types/toolbox').CatalogProvenance[]
      /** Page size (#482 Item 3). Backend defaults 50, max 500. */
      limit?: number
      /** Page offset (#482 Item 3). Backend defaults 0. */
      offset?: number
    },
  ): Promise<import('@/types/toolbox').ListCatalogEntriesResponse> {
    let url = `${CDB_TQS_API_BASE_URL}/source-property-catalog/sources/${sourceId}/entries`
    const queryParams = new URLSearchParams()
    if (opts?.provenance) queryParams.append('provenance', opts.provenance)
    if (opts?.provenances && opts.provenances.length > 0) {
      for (const p of opts.provenances) queryParams.append('provenances', p)
    }
    if (opts?.limit !== undefined)
      queryParams.append('limit', String(opts.limit))
    if (opts?.offset !== undefined)
      queryParams.append('offset', String(opts.offset))
    const qs = queryParams.toString()
    if (qs) url = `${url}?${qs}`
    const response = await routerFetch(url, { method: 'GET' })
    return response.data
  },

  async getSourcePropertyCatalogEntry(
    entryId: string,
  ): Promise<import('@/types/toolbox').SourcePropertyCatalogEntry> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/source-property-catalog/entries/${entryId}`,
      { method: 'GET' },
    )
    return response.data
  },

  async createSourcePropertyCatalogEntry(
    sourceId: string,
    data: import('@/types/toolbox').SourcePropertyCatalogEntryCreate,
  ): Promise<import('@/types/toolbox').SourcePropertyCatalogEntry> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/source-property-catalog/sources/${sourceId}/entries`,
      { method: 'POST', body: JSON.stringify(data) },
    )
    return response.data
  },

  async updateSourcePropertyCatalogEntry(
    entryId: string,
    patch: import('@/types/toolbox').SourcePropertyCatalogEntryUpdate,
  ): Promise<import('@/types/toolbox').SourcePropertyCatalogEntry> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/source-property-catalog/entries/${entryId}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    )
    return response.data
  },

  async deleteSourcePropertyCatalogEntry(entryId: string): Promise<void> {
    await routerFetch(
      `${CDB_TQS_API_BASE_URL}/source-property-catalog/entries/${entryId}`,
      { method: 'DELETE' },
    )
  },

  /** Mirror what the enrich-skill does at runtime — useful to verify
   *  alias coverage before promoting auto_derived entries.
   *
   *  When `subjectClassIri` is set the resolver narrows candidates by
   *  the entry's `valid_for_subject_class_iris` scope before picking,
   *  matching what the skill does for a given profile (#480). Leaving
   *  it undefined skips narrowing — useful to see the raw candidate
   *  set in the admin resolve-test box. */
  async resolveSourcePropertyCatalogTerms(
    sourceId: string,
    userTerms: string[],
    subjectClassIri?: string | null,
  ): Promise<import('@/types/toolbox').CatalogResolveResponse> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/source-property-catalog/sources/${sourceId}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify({
          user_terms: userTerms,
          subject_class_iri: subjectClassIri ?? undefined,
        }),
      },
    )
    return response.data
  },

  /** Status-Transition draft → testing → active (rückwärts blockt Backend). */
  async transitionEndpointStatus(
    id: string,
    status: import('@/types/toolbox').RouterEndpointStatus,
  ): Promise<import('@/types/toolbox').RouterEndpoint> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${id}/status`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
    )
    return response.data
  },

  // -------------------------------------------------------------------------
  // Endpoint Parameters
  // -------------------------------------------------------------------------

  async listEndpointParams(
    endpointId: string,
  ): Promise<import('@/types/toolbox').RouterEndpointParam[]> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${endpointId}/params`,
      { method: 'GET' },
    )
    return response.data
  },

  async createEndpointParam(
    endpointId: string,
    data: import('@/types/toolbox').RouterEndpointParamCreate,
  ): Promise<import('@/types/toolbox').RouterEndpointParam> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${endpointId}/params`,
      { method: 'POST', body: JSON.stringify(data) },
    )
    return response.data
  },

  async deleteEndpointParam(endpointId: string, paramId: string): Promise<void> {
    await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/endpoints/${endpointId}/params/${paramId}`,
      { method: 'DELETE' },
    )
  },

  /**
   * List unified tool-invocations (skills + router) from `tqs.tool_invocations`.
   *
   * Backend mounts this route under `/router/invocations` for historical
   * reasons, but the implementation reads from the shared audit table and
   * returns rows from ALL source_types (skill, router_rest, router_postgres).
   * The `source_type`-Filter is what scopes per module — see backend
   * `app/api/routes/router_invocations.py:list_router_invocations`.
   */
  async listToolInvocations(opts?: {
    sourceType?: import('@/types/toolbox').SourceType
    sourceRefId?: string
    callerUserId?: string
    callerApiKeyId?: string
    /** Filter by Communicator 2.0 conversation (`caller_session_id`).
     *  Backend filter param is a cdb_webos#309 B1 prerequisite — until it
     *  lands, the server ignores this and the History tab filters
     *  client-side. */
    callerSessionId?: string
    /** Filter by Communicator 2.0 turn — same #309 B1 prerequisite. */
    turnId?: string
    status?: import('@/types/toolbox').InvocationStatus
    mode?: import('@/types/toolbox').InvocationMode
    /** ISO-8601 datetime; rows with created_at >= since are returned. */
    since?: string
    /** ISO-8601 datetime; rows with created_at <= until are returned. */
    until?: string
    limit?: number
    offset?: number
  }): Promise<import('@/types/toolbox').ToolInvocation[]> {
    const params: Record<string, string | boolean> = {}
    if (opts?.sourceType) params.source_type = opts.sourceType
    if (opts?.sourceRefId) params.source_ref_id = opts.sourceRefId
    if (opts?.callerUserId) params.caller_user_id = opts.callerUserId
    if (opts?.callerApiKeyId) params.caller_api_key_id = opts.callerApiKeyId
    if (opts?.callerSessionId) params.caller_session_id = opts.callerSessionId
    if (opts?.turnId) params.turn_id = opts.turnId
    if (opts?.status) params.status = opts.status
    if (opts?.mode) params.mode = opts.mode
    if (opts?.since) params.since = opts.since
    if (opts?.until) params.until = opts.until
    // explizite undefined-Checks, sonst werden 0er-Werte verschluckt
    if (opts?.limit !== undefined) params.limit = String(opts.limit)
    if (opts?.offset !== undefined) params.offset = String(opts.offset)
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/invocations`,
      { method: 'GET', params },
    )
    return response.data
  },

  /** Fetch a single audit row by ID. Used by the test-panel after a
   *  `testRouterEndpoint` call to display the full persisted invocation
   *  (raw + projected response) in History-tab drill-downs. */
  async getToolInvocation(
    invocationId: string,
  ): Promise<import('@/types/toolbox').ToolInvocation> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/invocations/${invocationId}`,
      { method: 'GET' },
    )
    return response.data
  },

  /** Admin test-call — runs the connector without status / rate-limit
   *  gates. Audited with `mode='test'` so it stays visible in history but
   *  can be filtered out of production stats. Backend: `POST /router/test/{id}`.
   *
   *  Returns the sync `InvokeResponse` shape with `invocation_id` so the
   *  caller can drill into the audit row for raw/projected response. */
  async testRouterEndpoint(
    endpointId: string,
    params: Record<string, unknown> = {},
  ): Promise<import('@/types/toolbox').RouterInvokeResponse> {
    const response = await routerFetch(
      `${CDB_TQS_API_BASE_URL}/router/test/${endpointId}`,
      { method: 'POST', body: JSON.stringify({ params }) },
    )
    return response.data
  },

  async getAgentTools(opts?: {
    vendor?: string
    includeMetadata?: boolean
    simulateCallerType?: import('@/types/toolbox').CallerType
    simulateUserId?: string
  }): Promise<import('@/types/toolbox').OpenAIFunctionTool[]> {
    const params: Record<string, string | boolean> = {}
    if (opts?.vendor) params.vendor = opts.vendor
    if (opts?.includeMetadata !== undefined) params.include_metadata = opts.includeMetadata
    if (opts?.simulateCallerType) params.simulate_caller_type = opts.simulateCallerType
    if (opts?.simulateUserId) params.simulate_user_id = opts.simulateUserId
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/tools`,
      { method: 'GET', params },
    )
    return response.data
  },

  /**
   * Communicator 2.0 capability map for a (simulated) caller (cdb_tqs#481
   * Phase D-4): the permissions-filtered manifest after the 2.0 transforms
   * (hide primitives + collapse sources/corporate), grouped by Layer-1
   * capability role with collapsed/hidden markers + underlying permission
   * targets. capability_role = routing relevance, NOT access.
   */
  async getCapabilityMap(opts?: {
    simulateCallerType?: import('@/types/toolbox').CallerType
    simulateUserId?: string
  }): Promise<import('@/types/toolbox').CapabilityMapResponse> {
    const params: Record<string, string | boolean> = {}
    if (opts?.simulateCallerType) params.simulate_caller_type = opts.simulateCallerType
    if (opts?.simulateUserId) params.simulate_user_id = opts.simulateUserId
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/tools/capability-map`,
      { method: 'GET', params },
    )
    return response.data
  },
};
